'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { ToggleOption } from '@/interfaces';

interface SegmentedToggleProps {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export default function SegmentedToggle({
  options,
  value,
  onChange,
  className = '',
}: SegmentedToggleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<number | null>(null);
  const [startDragFromIndicator, setStartDragFromIndicator] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Fixed button width in pixels
  const buttonWidth = 45;
  const containerHeight = 32;
  const containerPadding = 2;
  const totalWidth = buttonWidth * options.length;
  const selectedIndex = options.findIndex(opt => opt.value === value);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const clickX = clientX - rect.left;

      // Calculate current indicator position
      const currentIndicatorLeft =
        selectedIndex * buttonWidth + containerPadding;
      const currentIndicatorRight = currentIndicatorLeft + buttonWidth;

      // Only start drag positioning if click is within the indicator area
      if (clickX >= currentIndicatorLeft && clickX <= currentIndicatorRight) {
        setIsDragging(true);
        setStartDragFromIndicator(true);
        // Store the offset from where user clicked within the indicator
        const offsetInIndicator = clickX - currentIndicatorLeft;
        setDragOffset(offsetInIndicator);
        // Don't set initial drag position - let it stay at current position until first move
      } else {
        // Click outside indicator - just switch to the clicked option immediately
        const clickedIndex = Math.min(
          Math.max(0, Math.floor((clickX - containerPadding) / buttonWidth)),
          options.length - 1
        );
        const newValue = options[clickedIndex].value;
        onChange(newValue);
        return;
      }
    }
  };

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !containerRef.current || !startDragFromIndicator)
        return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = clientX - rect.left - containerPadding;
      const indicatorX = mouseX - dragOffset; // Subtract the offset to maintain relative position
      const constrainedX = Math.max(
        0,
        Math.min(indicatorX, totalWidth - buttonWidth)
      ); // Constrain within bounds
      const percentX = (constrainedX / (totalWidth - buttonWidth)) * 100;
      setDragPosition(percentX);
    },
    [
      isDragging,
      startDragFromIndicator,
      totalWidth,
      buttonWidth,
      dragOffset,
      containerPadding,
    ]
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging || !containerRef.current) return;

    if (startDragFromIndicator && dragPosition !== null) {
      const relativePosition = (dragPosition / 100) * totalWidth;
      const selectedIndex = Math.min(
        Math.max(0, Math.round(relativePosition / buttonWidth)),
        options.length - 1
      );

      const newValue = options[selectedIndex].value;
      onChange(newValue);
    }

    setIsDragging(false);
    setDragPosition(null);
    setStartDragFromIndicator(false);
    setDragOffset(0);
  }, [
    isDragging,
    dragPosition,
    startDragFromIndicator,
    options,
    onChange,
    buttonWidth,
    totalWidth,
  ]);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
      const handleTouchMove = (e: TouchEvent) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchEnd = () => handleDragEnd();

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Calculate indicator position in pixels
  const indicatorPosition =
    isDragging && startDragFromIndicator && dragPosition !== null
      ? Math.min(
          Math.max(0, (dragPosition / 100) * (totalWidth - buttonWidth)),
          totalWidth - buttonWidth
        )
      : selectedIndex * buttonWidth;

  return (
    <div
      ref={containerRef}
      className={`relative rounded-lg select-none cursor-pointer hover:scale-102 transition-transform duration-200 ease-out ${className}`}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        height: `${containerHeight}px`,
        width: `${totalWidth + containerPadding * 2}px`,
        padding: `${containerPadding}px`,
        backgroundColor: 'transparent',
        borderColor: 'var(--color-border)',
      }}
    >
      {/* Sliding background indicator */}
      <div
        className={`absolute rounded-md shadow-sm border pointer-events-none ${
          isDragging
            ? 'transition-none'
            : 'transition-all duration-200 ease-out'
        }`}
        style={{
          width: `${buttonWidth}px`,
          left: `${indicatorPosition + containerPadding}px`,
          top: `${containerPadding}px`,
          bottom: `${containerPadding}px`,
          transform:
            isDragging && startDragFromIndicator ? 'scale(1.02)' : 'scale(1)',
          backgroundColor: 'var(--color-card-background)',
          borderColor: 'var(--color-card-border)',
          boxShadow: 'var(--shadow-sm)',
          padding: '4px 8px',
        }}
      />

      {/* Options */}
      {options.map(option => (
        <button
          key={option.value}
          onClick={() => !isDragging && onChange(option.value)}
          translate="no"
          className="relative z-10 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
          style={{
            width: `${buttonWidth}px`,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 4px',
            color:
              value === option.value && !isDragging
                ? 'var(--color-foreground)'
                : 'var(--color-foreground-secondary)',
          }}
          title={option.ariaLabel}
          aria-label={option.ariaLabel}
          aria-pressed={value === option.value}
        >
          <div className="flex items-center justify-center space-x-1 whitespace-nowrap">
            {option.icon && (
              <span className="flex items-center justify-center shrink-0">
                {option.icon}
              </span>
            )}
            {option.label && (
              <span className="flex items-center justify-center whitespace-nowrap">
                {option.label}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}
